/**
 * Crypto Price Caching Service
 *
 * Caches CoinGecko API responses in localStorage to reduce API calls.
 * Cache expires after 24 hours - crypto prices don't need to be real-time for comment context.
 */
import { getChain, normalizeChain } from './chainUtils';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_KEY_PREFIX = 'coincommons_price_';

/**
 * Get cached price from localStorage
 * @param {string} coinId - CoinGecko coin ID
 * @returns {object|null} Cached price data or null if not cached/expired
 */
const getCachedPrice = (coinId) => {
  try {
    const cacheKey = CACHE_KEY_PREFIX + coinId;
    const cached = localStorage.getItem(cacheKey);

    if (!cached) {
      return null;
    }

    const { price, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid (less than 24 hours old)
    if (now - timestamp < CACHE_DURATION) {
      return price;
    }

    // Cache expired, remove it
    localStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.warn('Error reading price cache:', error);
    return null;
  }
};

/**
 * Save price to localStorage cache
 * @param {string} coinId - CoinGecko coin ID
 * @param {number} price - USD price
 */
const setCachedPrice = (coinId, price) => {
  const cacheKey = CACHE_KEY_PREFIX + coinId;
  const cacheData = {
    price,
    timestamp: Date.now()
  };

  try {
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Error saving price cache:', error);
    // If localStorage is full, try to clear old price caches
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(CACHE_KEY_PREFIX))
        .forEach(key => localStorage.removeItem(key));

      // Retry saving
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (retryError) {
      // If still fails, just continue without caching
      console.warn('Could not save price cache even after cleanup');
    }
  }
};

/**
 * Fetch price from CoinGecko API
 * @param {string} coinId - CoinGecko coin ID
 * @returns {Promise<number|null>} USD price or null if failed
 */
const fetchPriceFromAPI = async (coinId) => {
  try {
    const url = `https://api.coingecko.com/api/v3/simple/price?ids=${coinId}&vs_currencies=usd`;
    const response = await fetch(url);

    if (!response.ok) {
      console.warn(`CoinGecko API returned ${response.status} for ${coinId}`);
      return null;
    }

    const data = await response.json();
    const price = data[coinId]?.usd || null;

    return price;
  } catch (error) {
    // API call failed (network error, CORS, etc.)
    console.warn(`CoinGecko API error for ${coinId}:`, error.message);
    return null;
  }
};

/**
 * Get cryptocurrency price in USD with caching
 * Unified flow for both localhost and production:
 * 1. Check cache first
 * 2. If no cache, try CoinGecko API
 * 3. If API fails, use fallback prices
 *
 * @param {string} chain - chain name
 * @returns {Promise<number|null>} USD price or null if unavailable
 */
export const getPriceInUSD = async (chain) => {
  if (!chain) {
    return null;
  }

  // Convert chain to CoinGecko ID if needed
  const chainObject = getChain(chain);
  if (!chainObject) {
    console.warn(`Unknown chain: ${chain}`);
    return null;
  }
  const coinId = chainObject.coinGeckoId;

  // Step 1: Check cache first
  const cachedPrice = getCachedPrice(coinId);
  if (cachedPrice !== null) {
    return cachedPrice;
  }

  // Step 2: Cache miss - try fetching from API
  const price = await fetchPriceFromAPI(coinId);

  // If API succeeded, cache it and return
  if (price !== null) {
    setCachedPrice(coinId, price);
    return price;
  }

  // Step 3: API failed - use fallback price (don't cache fallback)
  const fallbackPrice = chainObject.defaultPriceUSD;
  if (fallbackPrice !== undefined) {
    return fallbackPrice;
  }

  console.warn(`No price available for ${coinId} (no API response, no fallback)`);
  return null;
};

/**
 * Get ETH price specifically (commonly used throughout the app)
 * @returns {Promise<number>} ETH price in USD (defaults to 0 if unavailable)
 */
export const getEthPrice = async () => {
  const price = await getPriceInUSD('ethereum');
  return price || 0;
};

// Stablecoins - hardcode price to $1
const STABLECOIN_SYMBOLS = ['USDC', 'USDT', 'DAI', 'BUSD', 'TUSD', 'USDP', 'GUSD', 'FRAX', 'LUSD', 'UST'];

// Map our chain names to CoinGecko asset platform IDs
// These are used for looking up token contract addresses in the platforms object
export const CHAIN_TO_COINGECKO_PLATFORM = {
  'ethereum': 'ethereum',
  'polygon': 'polygon-pos',
  'bsc': 'binance-smart-chain',
  'arbitrum': 'arbitrum-one',
  'optimism': 'optimistic-ethereum',
  'base': 'base',
  'avalanche': 'avalanche',
};

// Map token symbols to CoinGecko IDs for common tokens
const TOKEN_SYMBOL_TO_COINGECKO_ID = {
  'MATIC': 'matic-network',
  'POL': 'pol',  // Polygon rebranded from MATIC to POL
  'WETH': 'weth',
  'WBTC': 'wrapped-bitcoin',
  'LINK': 'chainlink',
  'UNI': 'uniswap',
  'AAVE': 'aave',
  'CRV': 'curve-dao-token',
  'MKR': 'maker',
  'SNX': 'havven',
  'COMP': 'compound-governance-token',
  'SUSHI': 'sushi',
  'YFI': 'yearn-finance',
  'LDO': 'lido-dao',
  'APE': 'apecoin',
  'SHIB': 'shiba-inu',
  'PEPE': 'pepe',
};

/**
 * Get token price in USD from CoinGecko API (legacy version)
 * Handles stablecoins (hardcoded to $1) and fetches other tokens from CoinGecko
 * @param {string} token - Token symbol (e.g., 'USDC', 'MATIC')
 * @param {string} tokenAddress - ERC20 token contract address (optional for stablecoins/known tokens)
 * @param {string} platform - Platform ID (default: 'ethereum')
 * @returns {Promise<number|null>} USD price or null if failed
 * @deprecated Use getTokenPrice instead which uses local coins collection
 */
export const getTokenPriceFromAPI = async (token, tokenAddress, platform = 'ethereum') => {
  const upperToken = token?.toUpperCase();

  // Check if it's a stablecoin first - no API call needed
  if (upperToken && STABLECOIN_SYMBOLS.includes(upperToken)) {
    return 1;
  }

  // Check if we have a known CoinGecko ID for this token symbol
  const coinGeckoId = upperToken ? TOKEN_SYMBOL_TO_COINGECKO_ID[upperToken] : null;

  // Determine cache key
  const cacheKey = coinGeckoId
    ? `${CACHE_KEY_PREFIX}coin_${coinGeckoId}`
    : tokenAddress
      ? `${CACHE_KEY_PREFIX}token_${platform}_${tokenAddress.toLowerCase()}`
      : null;

  if (!cacheKey) return null;

  // Check cache first
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { price, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return price;
      }
      localStorage.removeItem(cacheKey);
    }
  } catch (e) {
    // Ignore cache errors
  }

  // Fetch from CoinGecko
  try {
    let url;
    let price = null;

    if (coinGeckoId) {
      // Use simple price API for known tokens (more reliable)
      url = `https://api.coingecko.com/api/v3/simple/price?ids=${coinGeckoId}&vs_currencies=usd`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        price = data[coinGeckoId]?.usd || null;
      }
    } else if (tokenAddress) {
      // Fallback to contract address lookup
      url = `https://api.coingecko.com/api/v3/simple/token_price/${platform}?contract_addresses=${tokenAddress}&vs_currencies=usd`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        price = data[tokenAddress.toLowerCase()]?.usd || null;
      }
    }

    // Cache the result
    if (price !== null) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ price, timestamp: Date.now() }));
      } catch (e) {
        // Ignore cache errors
      }
    }

    return price;
  } catch (error) {
    console.warn(`Token price fetch failed for ${token || tokenAddress}:`, error.message);
    return null;
  }
};

/**
 * Get token price in USD from local coins collection
 * Looks up coin by platform address, returns current_price
 * Falls back to stablecoin check if not found in DB
 *
 * @param {string} token - Token symbol (e.g., 'USDC', 'MATIC')
 * @param {string} tokenAddress - ERC20 token contract address
 * @param {string} chain - Chain name (default: 'ethereum')
 * @returns {Promise<number|null>} USD price or null if not found
 */
export const getTokenPrice = async (token, tokenAddress, chain = 'ethereum') => {
  const upperToken = token?.toUpperCase();

  // Check if it's a stablecoin first - no DB lookup needed
  if (upperToken && STABLECOIN_SYMBOLS.includes(upperToken)) {
    return 1;
  }

  if (!tokenAddress) {
    return null;
  }

  const normalizedAddress = tokenAddress.toLowerCase();

  // Normalize our chain name, then map to CoinGecko platform ID
  let normalizedChain;
  try {
    normalizedChain = normalizeChain(chain);
  } catch {
    normalizedChain = chain;
  }
  const coinGeckoPlatform = CHAIN_TO_COINGECKO_PLATFORM[normalizedChain];
  if (!coinGeckoPlatform) {
    console.warn(`No CoinGecko platform mapping for chain: ${normalizedChain}`);
    return null;
  }

  // Check cache first (use coinGeckoPlatform in key for consistency)
  const cacheKey = `${CACHE_KEY_PREFIX}db_${coinGeckoPlatform}_${normalizedAddress}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { price, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return price;
      }
      localStorage.removeItem(cacheKey);
    }
  } catch (e) {
    // Ignore cache errors
  }

  // Look up in coins collection by platform address
  try {
    // Query coins where platforms.{coinGeckoPlatform} matches the address
    // The platforms object stores addresses directly: { "ethereum": "0x...", "polygon-pos": "0x..." }
    const coinsRef = collection(db, 'coins');
    const q = query(coinsRef, where(`platforms.${coinGeckoPlatform}`, '==', normalizedAddress));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const coinDoc = querySnapshot.docs[0];
      const coinData = coinDoc.data();
      const price = coinData.current_price || null;

      // Cache the result
      if (price !== null) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ price, timestamp: Date.now() }));
        } catch (e) {
          // Ignore cache errors
        }
      }

      return price;
    }

    return null;
  } catch (error) {
    console.warn(`Token price lookup failed for ${token || tokenAddress}:`, error.message);
    return null;
  }
};

/**
 * Batch get token prices from local coins collection
 * Efficiently fetches prices for multiple tokens in a single query
 *
 * @param {Array<{token: string, tokenAddress: string, chain?: string}>} tokens - Array of token info
 * @returns {Promise<Map<string, number>>} Map of tokenAddress -> price
 */
export const batchGetTokenPrices = async (tokens, chain = 'ethereum') => {
  const priceMap = new Map();

  if (!tokens || tokens.length === 0) {
    return priceMap;
  }

  // First pass: handle stablecoins immediately (no chain/DB dependency)
  const nonStableTokens = [];
  for (const tokenInfo of tokens) {
    const { token, tokenAddress } = tokenInfo;
    const upperToken = token?.toUpperCase();

    if (upperToken && STABLECOIN_SYMBOLS.includes(upperToken)) {
      if (tokenAddress) {
        priceMap.set(tokenAddress.toLowerCase(), 1);
      }
      continue;
    }
    nonStableTokens.push(tokenInfo);
  }

  // If only stablecoins, return early
  if (nonStableTokens.length === 0) {
    return priceMap;
  }

  // Normalize our chain name, then map to CoinGecko platform ID
  let normalizedChain;
  try {
    normalizedChain = normalizeChain(chain);
  } catch {
    normalizedChain = chain;
  }
  const coinGeckoPlatform = CHAIN_TO_COINGECKO_PLATFORM[normalizedChain];
  if (!coinGeckoPlatform) {
    console.warn(`No CoinGecko platform mapping for chain: ${normalizedChain}`);
    return priceMap; // Return with stablecoins already processed
  }

  // Second pass: check cache and collect tokens needing DB lookup
  const tokensToLookup = [];

  for (const tokenInfo of nonStableTokens) {
    const { tokenAddress } = tokenInfo;

    if (!tokenAddress) continue;

    const normalizedAddress = tokenAddress.toLowerCase();

    // Check cache first
    const cacheKey = `${CACHE_KEY_PREFIX}db_${coinGeckoPlatform}_${normalizedAddress}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { price, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          priceMap.set(normalizedAddress, price);
          continue;
        }
        localStorage.removeItem(cacheKey);
      }
    } catch (e) {
      // Ignore cache errors
    }

    tokensToLookup.push(normalizedAddress);
  }

  // If all tokens were resolved from cache/stablecoins, return early
  if (tokensToLookup.length === 0) {
    return priceMap;
  }

  // Batch query coins collection
  // Firestore 'in' queries are limited to 30 items, so we batch if needed
  const BATCH_SIZE = 30;

  try {
    for (let i = 0; i < tokensToLookup.length; i += BATCH_SIZE) {
      const batch = tokensToLookup.slice(i, i + BATCH_SIZE);

      const coinsRef = collection(db, 'coins');
      const q = query(coinsRef, where(`platforms.${coinGeckoPlatform}`, 'in', batch));
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach(doc => {
        const coinData = doc.data();
        const platforms = coinData.platforms || {};
        // The address is stored directly: platforms["ethereum"] = "0x..."
        const address = platforms[coinGeckoPlatform];

        if (address && typeof address === 'string') {
          const normalizedAddr = address.toLowerCase();
          const price = coinData.current_price || null;

          if (price !== null) {
            priceMap.set(normalizedAddr, price);

            // Cache the result
            const cacheKey = `${CACHE_KEY_PREFIX}db_${coinGeckoPlatform}_${normalizedAddr}`;
            try {
              localStorage.setItem(cacheKey, JSON.stringify({ price, timestamp: Date.now() }));
            } catch (e) {
              // Ignore cache errors
            }
          }
        }
      });
    }
  } catch (error) {
    console.warn('Batch token price lookup failed:', error.message);
  }

  return priceMap;
};

/**
 * Prefetch prices for commonly used coins
 * Call this on app initialization to warm up the cache
 * @param {string[]} chains - Array of chain names to prefetch
 */
export const prefetchPrices = async (chains = ['ethereum']) => {
  const promises = chains.map(chain => getPriceInUSD(chain));
  await Promise.all(promises);
};

/**
 * Clear all cached prices (useful for testing or manual refresh)
 */
export const clearPriceCache = () => {
  try {
    const keys = Object.keys(localStorage).filter(key => key.startsWith(CACHE_KEY_PREFIX));
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (error) {
    console.error('Error clearing price cache:', error);
    return false;
  }
};

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  window.clearCryptoPriceCache = clearPriceCache;
  window.prefetchCryptoPrices = prefetchPrices;
}
